"use client";

import React from "react";
import { Phone, ShieldCheck, Clock, FileSpreadsheet, Users, ArrowRight } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const faqItems = [{
  id: "item-1",
  question: "How rapidly can crews mobilize for multi-story after-hours emergencies?",
  answer: "Our technicians dispatch directly from regional stations in Maryland, DC, and Northern Virginia 24 hours a day, 365 days a year. Because we eliminate third-party call centers, you speak immediately with an active mitigation manager who routes commercial extraction units, desiccant dehumidifiers, and containment barriers to your site within 60 to 90 minutes. Initial work begins immediately to isolate water risers or hazardous zones before losses migrate between building floors.",
  badge: "60-90 Min Response",
  icon: Clock
}, {
  id: "item-2",
  question: "How do your teams coordinate communication with individual tenants and building occupants?",
  answer: "We partner directly with your on-site facility director or property manager to establish approved access protocols. Our team provides printed bilingual door notices, daily containment updates, and direct schedule confirmations so residents and commercial tenants know exactly when equipment will run or when units require access. Technicians arrive in marked commercial uniforms with photo credentials, ensuring tenant safety and minimal disruption.",
  badge: "Tenant Protocol",
  icon: Users
}, {
  id: "item-3",
  question: "What documentation is provided for HOA boards and commercial insurance adjusters?",
  answer: "Every project receives an itemized digital file updated every 24 hours. We document moisture mapping readings, thermal imaging scans, psychrometric drying logs, and line-item estimates written in Xactimate format — the exact software commercial carriers require. Board members and property adjusters receive clear before-and-after photographic proof alongside certified disposal manifests for structural materials and hazardous substances.",
  badge: "Xactimate & Photos",
  icon: FileSpreadsheet
}, {
  id: "item-4",
  question: "Can mitigation and repair work occur overnight to minimize commercial disruption?",
  answer: "Yes. Commercial retail, medical suites, and professional offices frequently require off-hours scheduling to protect daily revenue. We construct isolated negative-air containment zones with sound-dampening acoustic curtains and schedule heavy demolition or structural extraction during your closed hours. By the morning shift, common walkways are cleared, air scrubbers are secured, and facility staff can safely reopen accessible areas.",
  badge: "Off-Hours Scheduling",
  icon: ShieldCheck
}];
export default function Faqs() {
  const shouldReduceMotion = useReducedMotion();
  const entranceVariant = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.32,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="faqs" data-nav="light" className="relative border-b border-border bg-background py-16 text-foreground md:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Technical Spec Top Bar */}
        <div className="mb-10 flex flex-wrap items-center justify-between gap-4 border-b border-border pb-4">
          <div className="flex items-center gap-2">
            <span className="relative flex size-2.5">
              <span className="absolute inline-flex size-full animate-ping rounded-full bg-primary opacity-75" />
              <span className="relative inline-flex size-2.5 rounded-full bg-primary" />
            </span>
            <span className="font-mono text-xs font-semibold tracking-wider text-muted-foreground uppercase">
              24/7 Rapid Dispatch &bull; MD / DC / NOVA
            </span>
          </div>
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <span className="font-mono">Direct Local Crews &bull; Zero Call Centers</span>
          </div>
        </div>

        {/* Section Header */}
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} variants={entranceVariant} className="lg:col-span-5">
            <div className="inline-flex items-center rounded-md border border-primary/20 bg-primary/10 px-2.5 py-1 text-xs font-bold tracking-wider text-primary uppercase">
              Commercial &amp; HOA Guidance
            </div>
            <h2 className="mt-4 font-sans text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
              Commercial restoration questions answered.
            </h2>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground">
              Direct answers on crew mobilization, tenant notices, board documentation, and off-hours containment across Maryland, DC, and Northern Virginia.
            </p>

            <div className="mt-6 rounded-lg border border-border bg-muted/40 p-5">
              <h3 className="text-sm font-bold text-foreground">
                Need on-site commercial mitigation right now?
              </h3>
              <p className="mt-1 text-xs text-muted-foreground">
                Speak directly with an active project manager who can mobilize extraction equipment immediately.
              </p>
              <div className="mt-4 flex flex-col gap-2.5 sm:flex-row sm:items-center">
                <Button asChild className="bg-primary font-bold text-primary-foreground hover:bg-primary/90">
                  <a href="tel:855-435-6631">
                    <Phone className="size-4" />
                    <span>855-435-6631</span>
                  </a>
                </Button>
                <Button asChild variant="outline" className="border-border hover:bg-muted">
                  <Link to="/ContactRequestServicePage">
                    <span>Request site visit</span>
                    <ArrowRight className="size-3.5" />
                  </Link>
                </Button>
              </div>
            </div>
          </motion.div>

          {/* Accordion Panels */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} variants={entranceVariant} className="lg:col-span-7">
            <div className="rounded-lg border border-border bg-card p-4 sm:p-6 shadow-xs">
              <Accordion type="single" collapsible defaultValue="item-1" className="w-full">
                {faqItems.map((item, index) => {
                const IconComponent = item.icon;
                return <div data-index={index} key={item.id}>
                      <AccordionItem value={item.id} className="border-border">
                        <AccordionTrigger className="py-4 text-left text-base font-semibold hover:no-underline">
                          <div className="flex items-start gap-3 pr-2">
                            <div className="mt-0.5 rounded-md border border-primary/20 bg-primary/10 p-1.5 text-primary">
                              <IconComponent className="size-4" />
                            </div>
                            <div>
                              <div className="mb-1 inline-block rounded-sm bg-muted px-2 py-0.5 font-mono text-[11px] font-medium text-muted-foreground">
                                {item.badge}
                              </div>
                              <span className="block text-base font-semibold text-foreground">
                                {item.question}
                              </span>
                            </div>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="pt-1 pb-4 pl-11 text-sm leading-relaxed text-muted-foreground">
                          {item.answer}
                        </AccordionContent>
                      </AccordionItem>
                    </div>;
              })}
              </Accordion>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}